import { firebaseApp, auth } from '../config/firebase';
import { toast } from 'react-hot-toast';
import { saveUser, checkUserPhoneVerified } from '../api/users/saveUsers';
import Cookies from 'js-cookie';

/**
 * Sign in with popup (for OAuth providers)
 */
const SignInWithPopup = async (provider) => {
  try {
    const res = await firebaseApp.auth().signInWithPopup(provider);
    return res.user;
  } catch (err) {
    console.error('SignInWithPopup error:', err);
    throw err;
  }
};

/**
 * Sign up with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} displayName - User display name
 * @param {Object} [extra] - Extra fields merged into the create-user payload.
 *   Used by the KBT-174 invite flow (InviteRedemptionModal, reused as-is
 *   for the Membership 5787 "Already a member?" activation link) to pass
 *   the `inviteId` returned by /invites/verify, so the backend can
 *   securely apply the QuickBooks id already resolved for that verified
 *   invite (see users.controller.js).
 * @returns {Promise<{user: Object, requiresPhoneVerification: boolean}
 *   | {error: string, message: string}>} Never null. Both outcomes are
 *   truthy objects told apart by which key is present, so check
 *   `result.user` — `if (result)` is true even on failure. The sibling
 *   functions in socialAuth.js do return null; this one cannot, because
 *   callers need the Firebase error code to offer a specific recovery.
 */
const signUpWithEmail = async (email, password, displayName, extra = {}) => {
  try {
    // Create user with Firebase Auth
    const userCredential = await auth.createUserWithEmailAndPassword(email, password);
    const user = userCredential.user;

    // Update display name
    await user.updateProfile({ displayName });

    // Send email verification
    await user.sendEmailVerification();

    const payload = {
      email: user.email,
      displayName: displayName,
      uid: user.uid,
      emailVerified: false,
      loginType: 'email',
      ...extra,
    };

    // Save user to backend
    const response = await saveUser(payload);

    // Set cookies
    Cookies.set('uid', response.user.uid);
    Cookies.set('email', response.user.email);
    if (response.user.customerStripeID) {
      Cookies.set('customerStripeID', response.user.customerStripeID);
    }

    toast.success('Account created! Please verify your phone number.');

    return {
      user: { ...response.user, phoneVerified: false },
      requiresPhoneVerification: true,
    };
  } catch (error) {
    console.error('Sign up error:', error);

    // Handle specific Firebase errors
    const errorMessages = {
      'auth/email-already-in-use':
        'An account with this email already exists. Sign in with Google, Apple, or email instead.',
      'auth/invalid-email': 'Invalid email address',
      'auth/operation-not-allowed': 'Email/password accounts are not enabled',
      'auth/weak-password': 'Password should be at least 8 characters with uppercase, lowercase, number, and special character',
    };

    const message = errorMessages[error.code] || error.message || 'Failed to create account';
    toast.error(message);

    // The code is returned so a caller can offer a recovery specific to
    // it — EmailSignUpModal is about to use auth/email-already-in-use to
    // offer "sign in instead". Callers that only check `result.user`,
    // which today is InviteRedemptionModal, take the same branch this
    // took when it returned null.
    return { error: error.code || 'unknown', message };
  }
};

/**
 * Sign-in failures that are answered with one shared message on purpose, so
 * the form cannot be used to discover which addresses have accounts. These
 * are also the ones kept out of the console, for the same reason.
 */
const MASKED_SIGN_IN_CODES = [
  'auth/user-not-found',
  'auth/wrong-password',
  'auth/invalid-credential',
  'auth/user-disabled',
];

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<{user: Object, requiresPhoneVerification: boolean} | null>}
 */
const signInWithEmail = async (email, password) => {
  try {
    // Sign in with Firebase Auth
    const userCredential = await auth.signInWithEmailAndPassword(email, password);
    const user = userCredential.user;

    // Check if user has verified phone
    const existingUser = await checkUserPhoneVerified(user.uid);

    const payload = {
      email: user.email,
      displayName: user.displayName || email.split('@')[0],
      uid: user.uid,
      emailVerified: user.emailVerified,
      loginType: 'email',
    };

    // Save/update user in backend
    const response = await saveUser(payload);

    // Set cookies
    Cookies.set('uid', response.user.uid);
    Cookies.set('email', response.user.email);
    if (response.user.customerStripeID) {
      Cookies.set('customerStripeID', response.user.customerStripeID);
    }

    if (existingUser && existingUser.phoneVerified) {
      // Returning user with verified phone
      toast.success('Welcome back!');
      Cookies.set('phoneVerified', 'true');
      return {
        user: { ...response.user, phoneVerified: true },
        requiresPhoneVerification: false,
      };
    } else {
      // User needs phone verification
      toast.success('Signed in! Please verify your phone number.');
      return {
        user: { ...response.user, phoneVerified: false },
        requiresPhoneVerification: true,
      };
    }
  } catch (error) {
    // Log nothing for the codes the toast deliberately conflates. The raw
    // error carries the distinguishing `.code`, so logging it would leave
    // "this address has no account" sitting in the console for anyone who
    // presses F12 — undoing in devtools what the shared wording below does
    // on screen. Same reasoning as sendPasswordReset.
    if (!MASKED_SIGN_IN_CODES.includes(error.code)) {
      console.error('Sign in error:', error);
    }

    // Handle specific Firebase errors
    const errorMessages = {
      'auth/invalid-email': 'Invalid email address',
      // Same wording as a wrong password, for the reason in
      // PASSWORD_RESET_NOTICE — do not restore the distinction.
      // Disabled is in the same set: saying "this account is disabled"
      // confirms the address exists.
      'auth/user-disabled': 'Invalid email or password',
      'auth/user-not-found': 'Invalid email or password',
      'auth/wrong-password': 'Invalid email or password',
      'auth/invalid-credential': 'Invalid email or password',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    };

    const message = errorMessages[error.code] || error.message || 'Failed to sign in';
    toast.error(message);
    return null;
  }
};

/**
 * Message shown for every reset attempt that is not a client-side mistake.
 *
 * Deliberately says nothing about whether the address has an account. The
 * previous wording ("No account found with this email") let anyone test
 * addresses against the membership. Firebase also stops throwing
 * auth/user-not-found once email-enumeration protection is enabled, so
 * branching on it was unreliable as well as leaky.
 */
const PASSWORD_RESET_NOTICE = "If an account exists for that address, we've sent a reset link.";

/**
 * Send a branded password-reset email via the backend (SendGrid from
 * office@kbtla.org, link to kbtla.org/reset-password). Firebase's own
 * sendPasswordResetEmail is not used — that mail comes from a
 * firebaseapp.com address and is what was landing in spam.
 * @param {string} email - User email
 * @returns {Promise<boolean>} true when the member should be told to check
 *   their inbox — including when no such account exists.
 */
const sendPasswordReset = async (email) => {
  try {
    const res = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (res.status === 422) {
      toast.error('Invalid email address');
      return false;
    }

    if (!res.ok) {
      toast.error('Could not send the reset email. Please try again.');
      return false;
    }

    toast.success(PASSWORD_RESET_NOTICE);
    return true;
  } catch (error) {
    console.error('Password reset error:', error);
    toast.error('Could not send the reset email. Please try again.');
    return false;
  }
};

export default SignInWithPopup;
export { signUpWithEmail, signInWithEmail, sendPasswordReset };